import { createContext, useContext, useMemo } from 'react'
import { useSuperAdminTaskDetails } from '~/hooks/super-admin/useSuperAdminTaskDetails'
import { TaskObject } from '~/types/TaskType'
import { Message } from '~/components/chat/message'

interface SuperAdminTaskContextValue {
  task: TaskObject | undefined
  messages: Message[]
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export const SuperAdminTaskContext = createContext<SuperAdminTaskContextValue | undefined>(
  undefined
)

interface SuperAdminTaskProviderProps {
  children: React.ReactNode
  taskId: number
}

export function SuperAdminTaskProvider({ children, taskId }: SuperAdminTaskProviderProps) {
  // For super admin, we fetch task details directly by task ID
  // We don't have a conversation UUID in this context, so we pass null
  const { data: taskData, isFetching: isLoading, error, refetch } = useSuperAdminTaskDetails(taskId)

  const contextValue: SuperAdminTaskContextValue = useMemo(
    () => ({
      task: taskData?.task,
      messages: taskData?.messages ?? [],
      isLoading,
      error,
      refetch
    }),
    [taskData, isLoading, error, refetch]
  )

  return (
    <SuperAdminTaskContext.Provider value={contextValue}>{children}</SuperAdminTaskContext.Provider>
  )
}

// Hook to use super admin task context
export function useSuperAdminTask(): SuperAdminTaskContextValue {
  const context = useContext(SuperAdminTaskContext)
  if (!context) {
    throw new Error('useSuperAdminTask must be used within a SuperAdminTaskProvider')
  }
  return context
}
